import { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.css";

const Tooltip = ({
    children,
    text,
    position = "bottom",
    tooltipWrapperStyle = {},
}: {
    children: React.ReactNode;
    text: string | "object";
    position?: "bottom" | "top";
    tooltipWrapperStyle?: CSSProperties;
}) => {
    const [visible, setVisible] = useState(false);
    const [_position, setPosition] = useState({ top: 0, left: 0 });
    const childRef = useRef<HTMLDivElement>(null);
    const tooltipTextRef = useRef<HTMLDivElement>(null);
    const showTooltip = () => {
        if (childRef.current) {
            const rect = childRef.current.getBoundingClientRect();
            const extraOffSet = tooltipTextRef?.current?.offsetHeight || 0;
            setPosition({
                top: rect[position] + (position == "bottom" ? 5 : -extraOffSet - 10), // Adjust as needed
                left: rect.left + window.scrollX + rect.width / 2,
            });
            setVisible(true);
        }
    };

    const hideTooltip = () => setVisible(false);

    const toggleTooltip = () => {
        if (visible) {
            hideTooltip();
        } else {
            showTooltip();
        }
    };

    // Optional: close tooltip on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            if (childRef.current && !childRef.current.contains(e.target as Node)) {
                hideTooltip();
            }
        };
        if (visible) {
            document.addEventListener("click", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [visible]);

    return (
        <>
            <div
                ref={childRef}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onClick={toggleTooltip} // for mobile tap
                className={styles["tooltip-wrapper"]}
                style={{ ...tooltipWrapperStyle }}
            >
                {children}
            </div>

            {!!text?.length &&
                createPortal(
                    <div
                        className={`${styles["tooltip-box"]} ${position == "top" && styles["tooltip-box-top"]}`}
                        ref={tooltipTextRef}
                        style={{
                            position: "fixed",
                            top: _position.top,
                            left: _position.left,
                            transform: "translate(-50%, 0%)",
                            zIndex: 99999,
                            // @ts-expect-error required
                            "--arrowPos": position,
                            lineHeight: "15px",
                            visibility: visible ? "visible" : "hidden",
                        }}
                    >
                        {text}
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default Tooltip;
